import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Send, Phone, User, MessageSquare, Sparkles, Check, AlertCircle } from "lucide-react";
import "../css/Contact.css";

const css = `
@import url('https://fonts.googleapis.com/css2?family=Unbounded:wght@500;700;900&family=Manrope:wght@400;500;700&display=swap');

.ct{
  --bg:oklch(0.14 0.025 270); --fg:oklch(0.97 0.01 270); --mute:oklch(0.72 0.03 270);
  --a1:oklch(0.62 0.26 265); --a2:oklch(0.82 0.14 210); --a3:oklch(0.72 0.22 330); --line:oklch(1 0 0 / .1);
  position:relative; width:100%; padding:140px 24px; background:var(--bg); color:var(--fg);
  font-family:'Manrope',system-ui,sans-serif; overflow:hidden; isolation:isolate;
}
.ct *{ box-sizing:border-box; }
.ct .glow{ position:absolute; border-radius:50%; filter:blur(100px); z-index:-1; pointer-events:none; animation:ctDrift 20s ease-in-out infinite alternate; }
.ct .glow.g1{ width:520px; height:520px; background:var(--a1); opacity:.35; left:-8%; top:10%; }
.ct .glow.g2{ width:420px; height:420px; background:var(--a3); opacity:.22; right:-6%; bottom:-6%; animation-duration:26s; }
@keyframes ctDrift{ to{ transform:translate(60px,-50px) scale(1.15); } }

.ct-grid{ max-width:1180px; margin:0 auto; display:grid; grid-template-columns:5fr 7fr; gap:72px; align-items:center; }

.ct-pill{ display:inline-flex; align-items:center; gap:10px; padding:9px 16px; border-radius:99px; border:1px solid var(--line);
  background:oklch(1 0 0 / .05); backdrop-filter:blur(12px); font-size:.85rem; color:var(--mute); }
.ct h2{ font-family:'Unbounded',sans-serif; font-weight:700; letter-spacing:-0.045em; line-height:1.02; font-size:clamp(2.2rem,5.2vw,4.4rem); margin:26px 0 22px; }
.ct .grad{ background:linear-gradient(100deg,var(--a2),var(--a1) 45%,var(--a3)); -webkit-background-clip:text; background-clip:text; color:transparent; }
.ct .desc{ color:var(--mute); font-size:1.1rem; line-height:1.7; max-width:440px; }

.ring{ position:relative; width:132px; height:132px; margin-top:44px; display:grid; place-items:center; }
.ring::before{ content:""; position:absolute; inset:0; border-radius:50%; padding:2px;
  background:conic-gradient(from var(--ang,0deg),transparent 55%,var(--a2),var(--a1),var(--a3));
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0); -webkit-mask-composite:xor; mask-composite:exclude;
  animation:ctSpin 5s linear infinite; }
.ring span{ width:84px; height:84px; border-radius:50%; display:grid; place-items:center; background:oklch(1 0 0 / .06); border:1px solid var(--line); animation:ctFloat 4s ease-in-out infinite; }
@property --ang{ syntax:'<angle>'; inherits:false; initial-value:0deg; }
@keyframes ctSpin{ to{ --ang:360deg; } }
@keyframes ctFloat{ 50%{ transform:translateY(-8px) rotate(-6deg); } }

/* card */
.card{ position:relative; border-radius:36px; padding:clamp(26px,4.5vw,52px); border:1px solid var(--line); overflow:hidden;
  background:linear-gradient(160deg,oklch(1 0 0 / .09),oklch(1 0 0 / .025)); backdrop-filter:blur(24px); box-shadow:0 40px 80px -40px #000; }
.card::before{ content:""; position:absolute; inset:0; pointer-events:none; opacity:0; transition:opacity .35s;
  background:radial-gradient(420px circle at var(--x,50%) var(--y,50%), oklch(0.62 0.26 265 / .22), transparent 65%); }
.card:hover::before, .card:focus-within::before{ opacity:1; }
.card form{ position:relative; display:grid; gap:18px; }

/* floating-label fields */
.fld{ position:relative; }
.fld svg{ position:absolute; left:18px; top:20px; color:var(--mute); transition:color .25s, transform .3s; pointer-events:none; }
.fld input,.fld textarea{ width:100%; padding:26px 18px 12px 52px; border-radius:18px; border:1px solid var(--line); background:oklch(1 0 0 / .04);
  color:var(--fg); font:500 1rem 'Manrope',sans-serif; outline:none; resize:none; transition:border-color .25s, background .25s, box-shadow .25s; }
.fld label{ position:absolute; left:52px; top:19px; color:var(--mute); font-size:1rem; pointer-events:none; transform-origin:left top; transition:transform .25s cubic-bezier(.2,.8,.2,1), color .25s; }
.fld input:focus,.fld textarea:focus{ border-color:var(--a1); background:oklch(1 0 0 / .07); box-shadow:0 0 0 4px oklch(0.62 0.26 265 / .18); }
.fld input:focus ~ svg,.fld textarea:focus ~ svg{ color:var(--a2); transform:scale(1.1); }
.fld input:focus ~ label,.fld input:not(:placeholder-shown) ~ label,
.fld textarea:focus ~ label,.fld textarea:not(:placeholder-shown) ~ label{ transform:translateY(-12px) scale(.78); color:var(--a2); }

/* button */
.send{ position:relative; overflow:hidden; display:flex; align-items:center; justify-content:center; gap:12px; width:100%; padding:20px; border:0; border-radius:18px; cursor:pointer;
  font:700 1.02rem 'Manrope',sans-serif; color:#fff; background:linear-gradient(110deg,var(--a1),var(--a3)); box-shadow:0 14px 44px -14px var(--a1);
  transition:transform .25s, box-shadow .3s, opacity .3s; }
.send::after{ content:""; position:absolute; top:0; left:-60%; width:40%; height:100%; background:linear-gradient(100deg,transparent,oklch(1 0 0 / .35),transparent); transform:skewX(-20deg); transition:left .7s; }
.send:hover{ transform:translateY(-3px); box-shadow:0 20px 56px -12px var(--a3); } .send:hover::after{ left:130%; }
.send:active{ transform:scale(.98); } .send:disabled{ opacity:.6; cursor:wait; }
.send:hover svg{ transform:translate(4px,-4px); } .send svg{ transition:transform .3s; }
.spin{ width:22px; height:22px; border-radius:50%; border:2px solid oklch(1 0 0 / .3); border-top-color:#fff; animation:ctRot .8s linear infinite; }
@keyframes ctRot{ to{ transform:rotate(360deg); } }

.note{ display:flex; align-items:center; gap:12px; padding:14px 16px; border-radius:16px; font-size:.95rem; font-weight:600; animation:ctPop .5s cubic-bezier(.16,1.2,.3,1) both; }
.note.ok{ background:oklch(0.75 0.17 155 / .14); border:1px solid oklch(0.75 0.17 155 / .35); color:oklch(0.85 0.15 155); }
.note.bad{ background:oklch(0.68 0.22 20 / .14); border:1px solid oklch(0.68 0.22 20 / .35); color:oklch(0.82 0.15 20); }
@keyframes ctPop{ from{ opacity:0; transform:translateY(12px) scale(.96); } }

.rv{ opacity:0; transform:translateY(34px); filter:blur(6px); transition:opacity .9s, transform .9s cubic-bezier(.16,1,.3,1), filter .9s; }
.rv.on{ opacity:1; transform:none; filter:none; }
.ct :focus-visible{ outline:2px solid var(--a2); outline-offset:3px; }

@media(max-width:900px){ .ct{ padding:90px 20px; } .ct-grid{ grid-template-columns:1fr; gap:48px; } .ring{ display:none; } }
@media (prefers-reduced-motion:reduce){
  .ct *,.ct *::before,.ct *::after{ animation-duration:.01ms !important; animation-iteration-count:1 !important; transition-duration:.01ms !important; }
  .rv{ opacity:1; transform:none; filter:none; }
}
`;

function useReveal() {
  const ref = useRef(null);
  const [on, setOn] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setOn(true); io.disconnect(); } }, { threshold: 0.15 });
    io.observe(el); return () => io.disconnect();
  }, []);
  return [ref, on];
}

function ContactSection() {
  const { t } = useTranslation();
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [leftRef, leftOn] = useReveal();
  const [cardRef, cardOn] = useReveal();

  const spot = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    e.currentTarget.style.setProperty("--x", e.clientX - r.left + "px");
    e.currentTarget.style.setProperty("--y", e.clientY - r.top + "px");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(false);
    setSuccess(false);

    const form = e.target;
    const formData = new FormData(form);

    try {
      const res = await fetch("https://formsubmit.co/ajax/duruoguz5451@gmail.com", {
        method: "POST",
        body: formData,
        headers: { Accept: "application/json" },
      });
      if (res.ok) {
        setSuccess(true);
        form.reset();
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="ct">
      <style>{css}</style>
      <div className="glow g1" aria-hidden="true" />
      <div className="glow g2" aria-hidden="true" />

      <div className="ct-grid">
        {/* SOL */}
        <div ref={leftRef} className={`rv ${leftOn ? "on" : ""}`}>
          <div className="ct-pill">
            <Sparkles size={14} style={{ color: "var(--a2)" }} />
            {t("contact")}
          </div>
          <h2><span className="grad">{t("code_your_dreams")}</span></h2>
          <p className="desc">
            {t("contact_desc") ||
              "Bir fikrin mi var? Ya da markanı dijital dünyada zirveye mi taşımak istiyorsun? Formu doldur, kahvemizi içerken konuşalım."}
          </p>
          <div className="ring" aria-hidden="true"><span><Send size={30} /></span></div>
        </div>

        {/* SAĞ */}
        <div ref={cardRef} className={`rv card ${cardOn ? "on" : ""}`} style={{ transitionDelay: ".12s" }} onMouseMove={spot}>
          <form onSubmit={handleSubmit}>
            <input type="hidden" name="_captcha" value="false" />

            <div className="fld">
              <input id="ct-name" type="text" name="name" placeholder=" " required autoComplete="name" />
              <label htmlFor="ct-name">{t("contact_name")}</label>
              <User size={20} />
            </div>

            <div className="fld">
              <input id="ct-phone" type="tel" name="phone" placeholder=" " required autoComplete="tel" />
              <label htmlFor="ct-phone">{t("contact_phone")}</label>
              <Phone size={20} />
            </div>

            <div className="fld">
              <textarea id="ct-msg" name="message" rows="5" placeholder=" " required />
              <label htmlFor="ct-msg">{t("contact_message")}</label>
              <MessageSquare size={20} />
            </div>

            {success && (
              <div className="note ok" role="status"><Check size={18} /> {t("contact_success")}</div>
            )}
            {error && (
              <div className="note bad" role="alert"><AlertCircle size={18} /> {t("contact_error")}</div>
            )}

            <button type="submit" disabled={loading} className="send">
              {loading ? <span className="spin" /> : (<>{t("contact_btn")} <Send size={18} /></>)}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}

export default ContactSection;